import { Box, InputBase, useTheme } from "@mui/material";
import { useState, useEffect } from "react";

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { themeSettings, tokens } from "../../../theme";
import SHModal from "../../theme/Modal/SHModal";
import Shbutton from "../../theme/buttons/Shbutton";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import { useTranslation } from "react-i18next";
import { useGetRelationInvoiceProductsQuery } from "../../../newapi/global/relation/relationSlice";
import { useLinkedObjectUpdateProductsMutation } from "../../../newapi/financial/invoiceSlice";
import { showToastMessagePostRequest, showToastMessage, showToastMessageRequestError } from "../../../utils/toasts";
import { InputDropdown } from "../../theme/dropdowns/InputDropdown";
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton } from "@mui/material";
import { EuroOutlined } from "@mui/icons-material";

const MUpdateOrderInvoiceProducts = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [orderProducts, setOrderProducts] = useState([]);
    const [nextRowIndex, setNextRowIndex] = useState(0);
    
    const { data: relationProducts, productsLoading } = useGetRelationInvoiceProductsQuery({id: props.orderData?.relationId, onlyActive: true});

    useEffect(() => {
        if (props.orderData?.linkedCosts && props.open) {
            const existingProducts = props.orderData.linkedCosts.filter(x => x.productSubType !== "fuel_surcharges").map((product, index) => ({
                ...product,
                rowIndex: index
            }));
            setOrderProducts(existingProducts);
            setNextRowIndex(existingProducts.length);
        }
    }, [props.orderData, props.open]);

    const handleClose = () => {
        setOrderProducts([]);
        setNextRowIndex(0);
        props.handleClose();
    }


    useEffect(() => {
        // Check if there's an empty new row (isNew: true but no product selected)
        const hasEmptyNewRow = orderProducts.find(x => x.isNew && !x.relationInvoiceProductId && !x.isDeleted);
        
        if (!hasEmptyNewRow) {
            const newProduct = {
                id: 0,
                rowIndex: nextRowIndex,
                amount: 0,
                description: "",
                price: "",
                isNew: true
            };
            setOrderProducts(prev => [...prev, newProduct]);
            setNextRowIndex(prev => prev + 1);
        }
    }, [orderProducts, nextRowIndex]);

    const [updateLinkedObjectProducts,{isLoading}] = useLinkedObjectUpdateProductsMutation();
    const SaveOrderProducts = async () => {
        try {
            if (isLoading) return;

            const incompleteLine = orderProducts.find(line => (parseInt(line.amount) === 0 || line.amount === "") && (line.id > 0 || line.relationInvoiceProductId));
            if (incompleteLine) {
                showToastMessage("error", `${t("incomplete_line")} - ${incompleteLine.description}`);
                return;
            }

            const mappedProducts = orderProducts.filter(line => (line.id > 0 || line.relationInvoiceProductId)).map(line => ({
                id: line.id,
                amount: parseInt(line.amount),
                description: line.description,
                price: line.price,
                ...(line.invoiceProductId && { invoiceProductId: line.invoiceProductId }),
                ...(line.relationInvoiceProductId && { relationInvoiceProductId: line.relationInvoiceProductId })
            }));

            const updateObject = {
                invoiceProducts: mappedProducts,
                linkedType: "order",
                linkedId: props.orderData?.id
            }

            const data = await updateLinkedObjectProducts(updateObject).unwrap()
            showToastMessagePostRequest(t, data);

            handleClose();
        } catch (err) {
            showToastMessageRequestError(t("update_failed"), t, err);
        }
    };

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    const handleDeleteLineRow = (rowIndex) => {
        setOrderProducts(prev => prev.filter(x => x.rowIndex !== rowIndex));
    };

    const handleChange = (field, rowIndex, value) => {
        if (!/^\d*\.?\d*$/.test(value)) {
            return;
        }

        setOrderProducts(prev => prev.map((line) => {
            if (line.rowIndex === rowIndex) {
                return {
                    ...line,
                    [field]: value
                };
            }
            return line;
        }));
    };

    const handleProductSelect = (rowIndex, selected) => {
        const isDuplicate = orderProducts.some(line => line.relationInvoiceProductId === selected.id && !line.isDeleted);
        if (isDuplicate) {
            showToastMessage("error", t("product_already_in_list"));
            return;
        }

        setOrderProducts(prev => prev.map((line) => {
            if (line.rowIndex === rowIndex) {
                return {
                    ...line,
                    relationInvoiceProductId: selected.id,
                    description: selected.name,
                    price: selected.categoryProduct?.price || selected.price || "",
                    showDropdown: false
                };
            }
            return line;
        }));
        
        // The useEffect will automatically add a new row since this product now has relationInvoiceProductId
    };

    return(
        <SHModal
            open={props.open}
            onClose={handleClose}
        >
            <Box 
                sx={{
                    textAlign:"center",
                    paddingY:4,
                    paddingX:5,
                    display:"flex",
                    flexDirection:"column",
                    gap:1
                }}>
                <Subheading>{t("update_order_invoice_products")}</Subheading>
            </Box>

            {/* BODY */}
            <Box sx={{maxHeight: "65vh", overflow: "auto", background: colors.white[400]}}>
                {orderProducts && orderProducts.length > 0 && orderProducts.filter(product => !product.isDeleted).map((orderProduct, index) => (
                    <Box key={"update-order-product-"+orderProduct.rowIndex}
                        sx={{
                            background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                            paddingRight:3,
                            paddingLeft:3,
                            paddingTop:1,
                            paddingBottom:1,
                            borderTopLeftRadius:16,
                            borderTopRightRadius:16,
                            display:"flex",
                            alignItems:"center",
                            gap:1
                        }}
                    >
                        <Box
                            sx={{
                                flex:1,
                                "@media screen and (max-width: 47.9375em)" : {
                                    flex:1
                                }
                            }}
                        >
                            <Box lineHeight={"40px"} width={"104px"}>
                                <Text>{orderProduct.isNew ? t("invoice_product") : t("description")}</Text>
                            </Box>
                            
                            {orderProduct.isNew && (orderProduct.showDropdown !== false) ? (
                                <Box display="flex"
                                    height="40px"
                                    sx={{
                                        marginTop:0.5,
                                        marginBottom:0.5,
                                        justifyContent:"space-between",
                                        "@media screen and (max-width: 47.9375em)" : {
                                            flexDirection:"column"
                                        }
                                    }}
                                >
                                    <InputDropdown
                                        height={40}
                                        searchOptions={{
                                            enabled: true,
                                            placeholder: t("search")
                                        }}
                                        styles={{
                                            minWidth:300
                                        }}
                                        displayName={"name"} 
                                        onChange={(selected) => handleProductSelect(orderProduct.rowIndex, relationProducts[selected])}
                                        noSelection={t("choose_option")}
                                        options={relationProducts ?? []}
                                        closeOnSelection
                                    />
                                </Box>
                            ) : (
                                <Box 
                                    sx={{
                                        display:"flex",
                                        alignItems:"center",
                                        background:colors.grey[100],
                                        borderRadius:5,
                                        height:40,
                                        flex:1,
                                        pl:2,
                                    }}
                                >
                                    <InputBase 
                                        sx={{
                                            px:2,
                                            flex:1, 
                                            color:colors.txt["primary"],
                                            lineHeight:"32px",
                                        }}
                                        value={orderProduct.description || ""}
                                        disabled={true}
                                    />  
                                </Box>
                            )}
                        </Box>
                        <Box
                            sx={{
                                flex:1,
                                "@media screen and (max-width: 47.9375em)" : {
                                    flex:1
                                }
                            }}
                        >
                            <Box lineHeight={"40px"} width={"104px"}>
                                <Text>{t("amount")}</Text>
                            </Box>
                            
                            <Box display="flex"
                                height="40px"
                                borderRadius="20px"
                                backgroundColor={colors.grey[100]}
                                sx={{
                                    marginTop:0.5,
                                    marginBottom:0.5,
                                    justifyContent:"space-between",
                                    "@media screen and (max-width: 47.9375em)" : {
                                        flexDirection:"column"
                                    }
                                }}
                            >
                            <InputBase 
                                    sx={{
                                        px:2,
                                        flex:1, 
                                        color:colors.txt["primary"],
                                        lineHeight:"32px",
                                    }}
                                    value={orderProduct.amount}
                                    onChange={(e) => handleChange("amount", orderProduct.rowIndex, e.target.value)}
                                    disabled={orderProduct.isNew && !orderProduct.relationInvoiceProductId}
                                />  
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                flex:1,
                                "@media screen and (max-width: 47.9375em)" : {
                                    flex:1
                                }
                            }}
                        >
                            <Box lineHeight={"40px"} width={"104px"}>
                                <Text>{t("price")}</Text>
                            </Box>
                            
                            <Box 
                                sx={{
                                    display:"flex",
                                    alignItems:"center",
                                    background:colors.grey[100],
                                    borderRadius:5,
                                    height:40,
                                    flex:1,
                                    pl:0.5,
                                }}
                            >
                                <Box
                                    sx={{
                                        background: themeSettings(theme.palette.mode).palette.primaryGrey.main,
                                        color:themeSettings(theme.palette.mode).palette.primaryGrey.contrastText,
                                        height:32,
                                        width:32,
                                        display:"flex",
                                        alignItems:"center",
                                        justifyContent:"center",
                                        borderRadius:4,
                                    }}
                                >
                                    <EuroOutlined fontSize="small"/>
                                </Box>
                            <InputBase 
                                    sx={{
                                        px:2,
                                        flex:1, 
                                        color:colors.txt["primary"],
                                        lineHeight:"32px",
                                    }}
                                    value={orderProduct.price || 0}
                                    onChange={(e) => handleChange("price", orderProduct.rowIndex, e.target.value)}
                                    disabled={orderProduct.isNew && !orderProduct.relationInvoiceProductId}
                                />  
                            </Box>
                        </Box>
                        
                        <Box
                            sx={{
                                "@media screen and (max-width: 47.9375em)" : {
                                    flex:1
                                }
                            }}
                        >
                            <Box lineHeight={"40px"} width={40}>
                                <Text>&nbsp;</Text>
                            </Box>

                            <Box display="flex"
                                height="40px"
                                borderRadius="20px"
                                sx={{
                                    
                                }}
                            >
                                <IconButton color="red" type="button" onClick={() => handleDeleteLineRow(orderProduct.rowIndex)} sx={{p:1}}>
                                    <DeleteIcon/>
                                </IconButton>
                            </Box>
                        </Box>
                    </Box>
                ))}
                
            </Box>
            <Box
                component="footer"
                sx={{
                    pt:2,
                    width:1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    display:"flex",
                    alignItems:"center",
                    justifyContent:"space-between",
                    paddingRight:3,
                    zIndex:1058,
                    position:"relative",
                    background:`${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius:24,
                    borderBottomRightRadius:24,
                    "@media screen and (max-width: 47.9375em)" : {
                        paddingRight:0,
                    }
                }}
            >
                <Shbutton isCancel={true} className={"always-visible"} onClick={handleClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon/>}>{t("cancel")}</Shbutton>
                <ButtonGroup version={2}> 
                    <Shbutton isLoading={isLoading} className={"group-btn"} 
                    color={"green"}
                    variant="contained" 
                    onClick={SaveOrderProducts}
                    >{t("update")}</Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>
    )
}

export default MUpdateOrderInvoiceProducts;